import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import type { ComponentPropsWithoutRef, ElementRef } from "react"
import { forwardRef } from "react"
import { cn } from "../utils"

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogPortal = DialogPrimitive.Portal
const DialogClose = DialogPrimitive.Close

const DialogOverlay = forwardRef<
	ElementRef<typeof DialogPrimitive.Overlay>,
	ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Overlay
		className={cn(
			"fixed inset-0 z-[var(--z-modal-backdrop)] bg-black/50",
			"data-[state=closed]:animate-out data-[state=open]:animate-in",
			"data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
			className,
		)}
		ref={ref}
		{...props}
	/>
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface DialogContentProps extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
	/** Hide the default close button */
	hideCloseButton?: boolean
	/** Custom className for the overlay */
	overlayClassName?: string
}

const DialogContent = forwardRef<ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
	({ className, children, hideCloseButton, overlayClassName, ...props }, ref) => (
		<DialogPortal>
			<DialogOverlay className={overlayClassName} />
			{/* Flex container for bulletproof centering */}
			<div className="pointer-events-none fixed inset-0 z-[var(--z-modal)] flex items-center justify-center p-4">
				<DialogPrimitive.Content
					className={cn(
						// Sizing
						"pointer-events-auto w-full max-w-lg",
						// Visual styling
						"overflow-hidden rounded-2xl border border-border-highlight bg-bg-card p-6 shadow-xl",
						// Animations
						"data-[state=closed]:animate-out data-[state=open]:animate-in",
						"data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
						"data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
						"duration-200",
						className,
					)}
					ref={ref}
					{...props}
				>
					{children}
					{!hideCloseButton && (
						<DialogPrimitive.Close className="absolute top-4 right-4 rounded-full p-2 text-text-muted transition-all hover:bg-bg-tertiary hover:text-text-primary focus:outline-none focus:ring-2 focus:ring-border-highlight active:scale-95 disabled:pointer-events-none">
							<X aria-hidden="true" className="size-4" />
							<span className="sr-only">Close</span>
						</DialogPrimitive.Close>
					)}
				</DialogPrimitive.Content>
			</div>
		</DialogPortal>
	),
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn("flex flex-col gap-1.5", className)} {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
	<div className={cn("flex justify-end gap-3", className)} {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = forwardRef<
	ElementRef<typeof DialogPrimitive.Title>,
	ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Title
		className={cn("text-balance font-semibold text-lg text-text-primary", className)}
		ref={ref}
		{...props}
	/>
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = forwardRef<
	ElementRef<typeof DialogPrimitive.Description>,
	ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
	<DialogPrimitive.Description
		className={cn("text-pretty text-sm text-text-secondary", className)}
		ref={ref}
		{...props}
	/>
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
}
